import type { OBCommentsReq } from "src/app/models/models";
import { CATEGORY_TOTALS } from "src/app/shared/constants/observation-categories";
import { ratingScale } from "src/app/shared/constants/rating-scales";
import type { IObservationRating, IPreviewDisabledObservation } from "./offline-rating-calculation.service";
import { aggregateHowOftenUiCountersPreview, completionFromUiCounter } from "./offline-rating-ui-counter.util";

/**
 * Offline rating calculation for ts_1 and ets_3 form types.
 * Mirrors backend RatingCalculationV2Strategy.
 *
 * Callers must pre-filter addRemoveOb events for reset maneuvers
 * before passing them here. See getPreviewSummaryData in
 * ts-rating-form.base.ts for the filtering logic.
 *
 * Pass `formVersion: 3` for TS-1 v3 spreadsheet How Many rounding (frac >= 0.61).
 */
export class OfflineRatingV2Calculator {
	constructor(private readonly options?: { formVersion?: number }) {}

	private get useTs1V3HowManyRounding(): boolean {
		return this.options?.formVersion === 3;
	}

	/**
	 * TS1-v3 How Many (BE parity): predefined unobserve 0/N must not mark the OB
	 * unobserved. How Often still receives those rows via the unfiltered newObs.
	 * Keys include candidateRole so PF/PM dual presets are matched separately.
	 */
	private filterNewObsForHowManyTs1V3(
		newObs: IObservationRating[],
		predefinedObs: IObservationRating[],
	): IObservationRating[] {
		if (!this.useTs1V3HowManyRounding) return newObs;
		const predefinedKeys = new Set(
			(predefinedObs ?? []).map((o) => this.howOftenObservationKey(o)),
		);
		return (newObs ?? []).filter((obs) => {
			if ((obs.activeAttempt ?? 0) > 0) return true;
			return !predefinedKeys.has(this.howOftenObservationKey(obs));
		});
	}

	/**
	 * Old TS1 / eTS-3 presets have no seat, so a PF/PM removal replaces that preset.
	 * TS1 v3 (formVersion 3) keeps the seat so PF and PM presets stay apart.
	 */
	private howOftenObservationKey(obs: {
		maneuverCode: string;
		observationCode: string;
		candidateRole?: string | null;
	}): string {
		if (this.useTs1V3HowManyRounding) {
			const role = (obs.candidateRole ?? "").trim();
			if (role) {
				return `${obs.maneuverCode}::${obs.observationCode}::${role}`;
			}
		}
		return `${obs.maneuverCode}::${obs.observationCode}`;
	}

	// --- helpers ---

	private filterObservations(
		predefinedObs: IObservationRating[],
		newObs: IObservationRating[],
		maneuverCodeToExclude: string[],
		observationDisabled: OBCommentsReq[],
	) {
		const isExcluded = (maneuverCode: string, observationCode: string) => {
			if (maneuverCodeToExclude?.includes(maneuverCode)) return true;
			return observationDisabled?.some((d) => d.maneuverCode === maneuverCode && d.observationCode === observationCode);
		};

		return {
			filteredPredefinedObs: predefinedObs?.filter(
				({ maneuverCode, observationCode }) => !isExcluded(maneuverCode, observationCode),
			),
			filteredNewObs: newObs?.filter(({ maneuverCode, observationCode }) => !isExcluded(maneuverCode, observationCode)),
		};
	}

	private filterObservationsPreview(
		predefinedObs: IObservationRating[],
		newObs: IObservationRating[],
		maneuverCodeToExclude: string[],
		disabledObservations: IPreviewDisabledObservation[],
	) {
		const isExcluded = (maneuverCode: string, observationCode: string) => {
			if (maneuverCodeToExclude?.includes(maneuverCode)) return true;
			return disabledObservations?.some(
				(d) => d.maneuverCode === maneuverCode && d.observationCode === observationCode,
			);
		};

		return {
			filteredPredefinedObs: predefinedObs?.filter(
				({ maneuverCode, observationCode }) => !isExcluded(maneuverCode, observationCode),
			),
			filteredNewObs: newObs?.filter(({ maneuverCode, observationCode }) => !isExcluded(maneuverCode, observationCode)),
		};
	}

	// --- Rating A: "How Many" — binary collapse per obs code, average category ratings ---

	calculateRatingA = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		observationDisabled = [],
		formTypeCode,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		observationDisabled: OBCommentsReq[];
		formTypeCode?: string;
	}): number => {
		const perCategory = this.calculationHowMany({
			newObs,
			predefinedObs,
			maneuverCodeToExclude,
			observationDisabled,
			formTypeCode,
		});
		const ratingA = Math.round(perCategory?.reduce((acc, { rating }) => acc + rating, 0) / perCategory?.length);
		return Number.isNaN(ratingA) ? 1 : ratingA;
	};

	/**
	 * Core "How Many" calculation for V2.
	 * Filters with runtime types, then delegates to rateHowManyCategories.
	 */
	private calculationHowMany = ({
		newObs,
		predefinedObs,
		maneuverCodeToExclude = [],
		observationDisabled = [],
		formTypeCode,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		observationDisabled: OBCommentsReq[];
		formTypeCode?: string;
	}): { obCategoryCode: string; rating: number }[] => {
		const howManyNewObs = this.filterNewObsForHowManyTs1V3(newObs, predefinedObs);
		const { filteredPredefinedObs, filteredNewObs } = this.filterObservations(
			predefinedObs,
			howManyNewObs,
			maneuverCodeToExclude,
			observationDisabled,
		);
		return this.rateHowManyCategories(filteredPredefinedObs, filteredNewObs, formTypeCode);
	};

	// R = (E + 1) / (E + I + 3), score = 4R + 1, round at 0.61
	// V2: frac > 0.61; TS1-v3 (formVersion 3): frac >= 0.61 per spreadsheet.
	private scoreNoPredefinedHowMany(ctx: { category: string; observed: number; unobserved: number }): number | null {
		if (!CATEGORY_TOTALS[ctx.category]) return null;

		const { observed: E, unobserved: I } = ctx;
		const r = (E + 1) / (E + I + 3);
		const score = 4 * r + 1;
		const frac = score - Math.floor(score);
		const shouldCeil = this.useTs1V3HowManyRounding ? frac >= 0.61 : frac > 0.61;
		return shouldCeil ? Math.ceil(score) : Math.floor(score);
	}

	// R = (P + 1) / (P + N + 3), score = 4R + 1, round at 0.61
	private scoreNoPredefinedHowOften(ctx: {
		category: string;
		observed: number;
		unobserved: number;
		maneuverCount: number;
		ratingACap: number | undefined;
	}): {
		rating: number;
		totalAttempts: number;
		activeAttempts: number;
		completionPercentage: number;
		observationCount: number;
	} | null {
		if (!CATEGORY_TOTALS[ctx.category]) return null;

		const { observed: P, unobserved: N, ratingACap } = ctx;
		const r = (P + 1) / (P + N + 3);
		const score = 4 * r + 1;
		const frac = score - Math.floor(score);
		const rawRating = frac >= 0.61 ? Math.ceil(score) : Math.floor(score);

		// Per-category cap: Rating B may exceed Rating A by at most 1 level.
		const rating = ratingACap !== undefined ? Math.min(rawRating, ratingACap + 1) : rawRating;

		const rawPct = P + N > 0 ? (P / (P + N)) * 100 : 0;

		return {
			rating,
			totalAttempts: P + N,
			activeAttempts: P,
			completionPercentage: +rawPct.toFixed(2),
			observationCount: P + N,
		};
	}

	/**
	 * Shared "How Many" rating logic (post-filter).
	 * Collapses observations across maneuvers into binary observed/applicable per obs code,
	 * groups by category, then dispatches per category:
	 *   - no-predefined (isV3Eligible && predefinedCount === 0): fixed-total scale + bump, cap at 5
	 *   - predefined: observed/applicable percentage → ratingScale
	 */
	private rateHowManyCategories(
		filteredPredefinedObs: IObservationRating[],
		filteredNewObs: IObservationRating[],
		formTypeCode?: string,
	): { obCategoryCode: string; rating: number }[] {
		const isV3Eligible = formTypeCode === "ts_1" || formTypeCode === "ets_3";

		// Build a lookup for newObs by maneuver+observation(+role) so PF/PM overrides stay separate
		const newObsMap = new Map<string, IObservationRating>();
		for (const obs of filteredNewObs) {
			newObsMap.set(this.howOftenObservationKey(obs), obs);
		}

		// Collapse across maneuvers — binary per observation code
		const observationMap = new Map<string, { isPredefined: boolean; isObserved: boolean; isAttempted: boolean }>();

		// For predefined obs: use newObs activeAttempt if overridden, otherwise predefined is unchanged
		for (const obs of filteredPredefinedObs) {
			const key = this.howOftenObservationKey(obs);
			const newObsEntry = newObsMap.get(key);
			const effectiveActiveAttempt = newObsEntry ? newObsEntry.activeAttempt : obs.activeAttempt;

			const existing = observationMap.get(obs.observationCode);
			if (existing) {
				existing.isPredefined = existing.isPredefined || obs.totalAttempt > 0;
				existing.isObserved = existing.isObserved || effectiveActiveAttempt > 0;
				existing.isAttempted = existing.isAttempted || obs.totalAttempt > 0;
			} else {
				observationMap.set(obs.observationCode, {
					isPredefined: obs.totalAttempt > 0,
					isObserved: effectiveActiveAttempt > 0,
					isAttempted: obs.totalAttempt > 0,
				});
			}
		}

		// Handle newObs entries not in predefined (activated undefined)
		for (const obs of filteredNewObs) {
			const existing = observationMap.get(obs.observationCode);
			if (existing) {
				existing.isObserved = existing.isObserved || obs.activeAttempt > 0;
				existing.isAttempted = existing.isAttempted || obs.totalAttempt > 0;
			} else {
				observationMap.set(obs.observationCode, {
					isPredefined: false,
					isObserved: obs.activeAttempt > 0,
					isAttempted: obs.totalAttempt > 0,
				});
			}
		}

		// Group by category with unobserved tracking
		const categoryMap = new Map<
			string,
			{ applicable: number; observed: number; unobserved: number; predefinedCount: number }
		>();

		for (const [observationCode, { isPredefined, isObserved, isAttempted }] of observationMap) {
			if (!isPredefined && !isObserved && !isAttempted) continue;

			const category = observationCode.split("_")[0].toUpperCase();
			const existing = categoryMap.get(category);

			if (existing) {
				existing.applicable++;
				if (isObserved) existing.observed++;
				else existing.unobserved++;
				if (isPredefined) existing.predefinedCount++;
			} else {
				categoryMap.set(category, {
					applicable: 1,
					observed: isObserved ? 1 : 0,
					unobserved: isObserved ? 0 : 1,
					predefinedCount: isPredefined ? 1 : 0,
				});
			}
		}

		// Rate per category with per-category dispatch
		const categoryRatings: { obCategoryCode: string; rating: number }[] = [];

		for (const [category, { applicable, observed, unobserved, predefinedCount }] of categoryMap) {
			if (isV3Eligible && predefinedCount === 0) {
				// No-predefined path: delegate to overridable method
				const rating = this.scoreNoPredefinedHowMany({ category, observed, unobserved });
				if (rating === null) continue;
				categoryRatings.push({ obCategoryCode: category, rating });
			} else {
				// Predefined path: observed/applicable → ratingScale
				const percentage = Math.round((observed / applicable) * 100);
				const rating = ratingScale(percentage);

				categoryRatings.push({ obCategoryCode: category, rating });
			}
		}

		return categoryRatings;
	}

	// --- Rating B: "How Often" — predefined set logic ---

	calculateRatingB = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		observationDisabled = [],
		formTypeCode,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		observationDisabled: OBCommentsReq[];
		formTypeCode?: string;
	}): number => {
		const perCategory = this.calculationHowOften({
			newObs,
			predefinedObs,
			maneuverCodeToExclude,
			observationDisabled,
			formTypeCode,
		});
		const ratingB = Math.round(perCategory?.reduce((acc, { rating }) => acc + rating, 0) / perCategory?.length);
		return Number.isNaN(ratingB) ? 1 : ratingB;
	};

	/**
	 * Core "How Often" calculation for V2.
	 * Filters with runtime types, then delegates to rateHowOftenCategories.
	 */
	private calculationHowOften = ({
		newObs,
		predefinedObs,
		maneuverCodeToExclude = [],
		observationDisabled = [],
		formTypeCode,
		ratingAByCategory,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		observationDisabled: OBCommentsReq[];
		formTypeCode?: string;
		ratingAByCategory?: Map<string, number>;
	}): {
		obCategoryCode: string;
		rating: number;
		totalAttempts: number;
		activeAttempts: number;
		completionPercentage: number;
		observationCount: number;
	}[] => {
		const { filteredPredefinedObs, filteredNewObs } = this.filterObservations(
			predefinedObs,
			newObs,
			maneuverCodeToExclude,
			observationDisabled,
		);
		return this.rateHowOftenCategories(
			filteredPredefinedObs,
			filteredNewObs,
			formTypeCode,
			ratingAByCategory,
			undefined,
		);
	};

	/**
	 * Shared "How Often" rating logic (post-filter).
	 * Uses two category maps to dispatch per category:
	 *   - predefinedCategoryMap: categories with predefined obs (observed/expected ratio)
	 *   - noPredefinedCategoryMap: ts_1 categories without predefined obs (observed/unobserved + bump)
	 */
	private rateHowOftenCategories(
		filteredPredefinedObs: IObservationRating[],
		filteredNewObs: IObservationRating[],
		formTypeCode?: string,
		ratingAByCategory?: Map<string, number>,
		orderedFilteredNewObs?: IObservationRating[],
	): {
		obCategoryCode: string;
		rating: number;
		totalAttempts: number;
		activeAttempts: number;
		completionPercentage: number;
		observationCount: number;
	}[] {
		const isV3Eligible = formTypeCode === "ts_1" || formTypeCode === "ets_3";

		// Per-category Rating A used to cap no-predefined Rating B per category.
		// Callers that already compute How Many should pass it to avoid recomputing.
		const ratingACapMap =
			ratingAByCategory ??
			new Map<string, number>(
				this.rateHowManyCategories(filteredPredefinedObs, filteredNewObs, formTypeCode).map(
					({ obCategoryCode, rating }) => [obCategoryCode, rating],
				),
			);

		// Build predefined set
		const predefinedSet = new Set<string>();
		for (const obs of filteredPredefinedObs) {
			if (obs.totalAttempt > 0) {
				predefinedSet.add(this.howOftenObservationKey(obs));
			}
		}

		// Build newObs key set
		const newObsKeySet = new Set<string>();
		for (const obs of filteredNewObs) {
			newObsKeySet.add(this.howOftenObservationKey(obs));
		}

		// Build categoriesWithPredefined: categories that have at least one predefined obs with totalAttempt > 0
		const categoriesWithPredefined = new Set<string>();
		for (const obs of filteredPredefinedObs) {
			if (obs.totalAttempt > 0) {
				categoriesWithPredefined.add(obs.observationCode.split("_")[0].toUpperCase());
			}
		}

		// Two category maps for dispatch
		const predefinedCategoryMap = new Map<string, { observed: number; expected: number }>();
		const noPredefinedCategoryMap = new Map<string, { observed: number; unobserved: number; maneuvers: Set<string> }>();
		const latestNoPredefinedObsMap = new Map<string, IObservationRating>();

		// Route predefined obs → predefinedCategoryMap
		for (const obs of filteredPredefinedObs) {
			if (obs.totalAttempt <= 0) continue;
			const category = obs.observationCode.split("_")[0].toUpperCase();
			const existing = predefinedCategoryMap.get(category) || { observed: 0, expected: 0 };
			existing.expected += obs.totalAttempt;

			const key = this.howOftenObservationKey(obs);
			if (!newObsKeySet.has(key)) {
				// Unchanged predefined — fully observed
				existing.observed += obs.totalAttempt;
			}

			predefinedCategoryMap.set(category, existing);
		}

		// Route newObs: dispatch to predefined or no-predefined map
		for (const obs of filteredNewObs) {
			const category = obs.observationCode.split("_")[0].toUpperCase();
			const key = this.howOftenObservationKey(obs);
			const isPredefined = predefinedSet.has(key);

			if (isV3Eligible && !categoriesWithPredefined.has(category)) {
				// Later entries for the same maneuver-observation pair represent
				// the final cumulative state and should overwrite earlier events.
				latestNoPredefinedObsMap.set(key, obs);
			} else {
				// Predefined path: observed += activeAttempt, add non-predefined to expected
				const existing = predefinedCategoryMap.get(category) || { observed: 0, expected: 0 };
				existing.observed += obs.activeAttempt;

				if (!isPredefined && obs.totalAttempt > 0) {
					existing.expected += obs.totalAttempt;
				}

				predefinedCategoryMap.set(category, existing);
			}
		}

		for (const obs of latestNoPredefinedObsMap.values()) {
			const category = obs.observationCode.split("_")[0].toUpperCase();
			const existing = noPredefinedCategoryMap.get(category) ?? {
				observed: 0,
				unobserved: 0,
				maneuvers: new Set<string>(),
			};
			const inactiveAttempts = Math.max(obs.totalAttempt - obs.activeAttempt, 0);

			existing.observed += obs.activeAttempt;
			existing.unobserved += inactiveAttempts;
			existing.maneuvers.add(obs.maneuverCode);

			noPredefinedCategoryMap.set(category, existing);
		}

		const categoryBreakdown: {
			obCategoryCode: string;
			rating: number;
			totalAttempts: number;
			activeAttempts: number;
			completionPercentage: number;
			observationCount: number;
		}[] = [];

		const categoriesEmittedAsNoPredefined = new Set<string>();

		// Rate predefined categories: (observed/expected)*100 → ratingScale
		for (const [category, { observed, expected }] of predefinedCategoryMap) {
			if (expected === 0) continue;
			const completionPercentage = (observed / expected) * 100;
			const rating = ratingScale(Math.round(completionPercentage));

			categoryBreakdown.push({
				obCategoryCode: category,
				rating,
				totalAttempts: expected,
				activeAttempts: observed,
				completionPercentage: +completionPercentage.toFixed(2),
				observationCount: observed + expected,
			});
		}

		// Rate no-predefined categories: delegate to overridable method
		for (const [category, { observed, unobserved, maneuvers }] of noPredefinedCategoryMap) {
			categoriesEmittedAsNoPredefined.add(category);
			const result = this.scoreNoPredefinedHowOften({
				category,
				observed,
				unobserved,
				maneuverCount: maneuvers.size,
				ratingACap: ratingACapMap.get(category),
			});
			if (result === null) continue;
			categoryBreakdown.push({ obCategoryCode: category, ...result });
		}

		if (orderedFilteredNewObs !== undefined) {
			const ui = aggregateHowOftenUiCountersPreview({
				filteredPredefinedObs,
				orderedNewObs: orderedFilteredNewObs,
				formTypeCode,
				matchBySeat: this.useTs1V3HowManyRounding,
			});
			for (const row of categoryBreakdown) {
				const pack = categoriesEmittedAsNoPredefined.has(row.obCategoryCode)
					? ui.noPredefinedBranch.get(row.obCategoryCode)
					: ui.predefinedBranch.get(row.obCategoryCode);
				if (!pack) continue;
				if (pack.totalAttempts <= 0 && pack.activeAttempts <= 0) continue;
				row.activeAttempts = pack.activeAttempts;
				row.totalAttempts = pack.totalAttempts;
				row.completionPercentage = completionFromUiCounter(pack.activeAttempts, pack.totalAttempts);
				row.observationCount = pack.totalAttempts;
				if (categoriesEmittedAsNoPredefined.has(row.obCategoryCode)) {
					const recalculated = this.scoreNoPredefinedHowOften({
						category: row.obCategoryCode,
						observed: pack.activeAttempts,
						unobserved: Math.max(pack.totalAttempts - pack.activeAttempts, 0),
						maneuverCount: noPredefinedCategoryMap.get(row.obCategoryCode)?.maneuvers.size ?? 0,
						ratingACap: ratingACapMap.get(row.obCategoryCode),
					});
					if (recalculated) {
						row.rating = recalculated.rating;
					}
				} else {
					row.rating = ratingScale(Math.round(row.completionPercentage));
				}
			}
		}

		return categoryBreakdown;
	}

	// --- Preview Breakdown methods (used by ts-rating-form.base.ts) ---

	ObservationBreakdownHowManyCalculation = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		disabledObservations = [],
		formTypeCode,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		disabledObservations?: IPreviewDisabledObservation[];
		formTypeCode?: string;
	}): { obCategoryCode: string; rating: number }[] => {
		const howManyNewObs = this.filterNewObsForHowManyTs1V3(newObs, predefinedObs);
		const { filteredPredefinedObs, filteredNewObs } = this.filterObservationsPreview(
			predefinedObs,
			howManyNewObs,
			maneuverCodeToExclude,
			disabledObservations ?? [],
		);
		return this.rateHowManyCategories(filteredPredefinedObs, filteredNewObs, formTypeCode);
	};

	ObservationBreakdownHowOftenCalculation = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		disabledObservations = [],
		formTypeCode,
		ratingAByCategory,
		orderedNewObs,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		disabledObservations?: IPreviewDisabledObservation[];
		formTypeCode?: string;
		ratingAByCategory?: Map<string, number>;
		/** Chronological post-reset history for UI counter replay only (optional). */
		orderedNewObs?: IObservationRating[];
	}): {
		obCategoryCode: string;
		rating: number;
		totalAttempts: number;
		activeAttempts: number;
		completionPercentage: number;
		observationCount: number;
	}[] => {
		const { filteredPredefinedObs, filteredNewObs } = this.filterObservationsPreview(
			predefinedObs,
			newObs,
			maneuverCodeToExclude,
			disabledObservations ?? [],
		);
		const orderedFiltered =
			orderedNewObs === undefined
				? undefined
				: this.filterObservationsPreview(
						predefinedObs,
						orderedNewObs,
						maneuverCodeToExclude,
						disabledObservations ?? [],
					).filteredNewObs;
		return this.rateHowOftenCategories(
			filteredPredefinedObs,
			filteredNewObs,
			formTypeCode,
			ratingAByCategory,
			orderedFiltered,
		);
	};
}
